<template>
  <FriedCard :id="milestone.id" :class="['milestone-card', { 'example-milestone': !milestone.id }]">
    <div class="progress-bar">
      <div class="progress-value" :style="{ width: getProgress(progress) }" />
    </div>
    <div class="milestone-content">
      <div :class="['milestone-header', { 'collapsed-header': !expanded }]">
        <div class="title-input">
          <FriedStatusBadge
            :status-type="getStatus(milestone)"
            :text="t('MAP_badge_milestone', { numbers: milestoneOrder })"
          />
          <div class="title">
            <FriedH3 class="map-title paragraph-large">
              {{ milestone.title }}
            </FriedH3>
            <FriedIconButton
              data-external="milestone-collapse-button"
              :disabled="!milestone.id"
              @click="expanded = !expanded"
            >
              <FriedIcon :class="{ expanded }" icon="chevron-down" />
            </FriedIconButton>
          </div>
        </div>
        <div class="milestone-action-container">
          <div class="badge-container">
            <div v-if="isCompleted(milestone)" class="check-mark-container">
              <FriedIcon :size="IconSize.Small" icon="bold-check-mark" />
            </div>
          </div>
          <div
            v-if="!isSmallScreenAndDown && milestone.tasks.length && taskOwners.length"
            class="owner-info"
          >
            <FriedAvatarGroup
              :avatars="taskOwners"
              :size="AvatarSize.Small"
              :visible-avatars="2"
              clickable
              @click="showTaskOwnersModal = true"
            />
          </div>
        </div>
      </div>
      <TransitionExpand>
        <div v-show="expanded">
          <InlineRichTextEditorViewer
            :content="formattedDescription"
            class="description-input-viewer"
            data-external="milestone-description-text"
          />
          <FriedH4 v-if="milestone.tasks.length > 0" class="task-title">{{
            `${t('MAP_tasks')} (${milestone.tasks.length})`
          }}</FriedH4>
          <TaskRow
            v-for="(task, index) in milestone.tasks"
            :id="task.id"
            :key="task.id"
            :task="task"
            :data-task-id="task.id"
            class="task-item"
            :task-order="index + 1"
            :owner="owner"
            :participants="participants"
            :participant-id="participantId"
            @click.native="handleOpenTaskDrawer(task)"
          />
        </div>
      </TransitionExpand>
    </div>
    <TransitionSlideInOut appear :origin="SlideOrigin.Right">
      <EditTaskDrawer
        v-if="showTaskDrawer"
        :task-to-edit="taskToEdit"
        :participants="participants"
        :participant-id="participantId"
        :owner="owner"
        @update-task-status="handleUpdateTaskStatus"
        @close="showTaskDrawer = false"
      />
    </TransitionSlideInOut>
    <ParticipantListModal
      v-if="showTaskOwnersModal"
      :participants="taskOwners"
      :modal-title="t('map_task_owners_modal_title')"
      show-title
      :current-user-id="participantId"
      show-current-user-label
      @close="showTaskOwnersModal = false"
    />
  </FriedCard>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { AvatarSize, IconSize, SlideOrigin, ButtonType } from '@getaccept/fried-tofu';
import ParticipantListModal from '@getaccept/dsr-shared-new/src/components/ParticipantListModal.vue';
import InlineRichTextEditorViewer from '@getaccept/dsr-shared-new/src/inline-text-editor/components/InlineRichTextEditorViewer.vue';
import TaskRow from '@getaccept/dsr-shared-new/src/mutual-action-plan/components/Task.vue';
import type { Milestone } from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/action-plan';
import type { Participant } from '@getaccept/dsr-shared-new';
import { MilestoneHelper } from '@getaccept/dsr-shared-new/src/mutual-action-plan/helpers/milestone.helper';
import type { TaskStatus } from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/enums/task-status';
import { useMilestone } from '@getaccept/dsr-shared-new/src/mutual-action-plan/composables/milestone.composable';
import { ParticipantRole } from '@getaccept/lib-shared-new/src/dealroom/types/enums/participant-role';
import EditTaskDrawer from './EditTaskDrawer.vue';

export default defineComponent({
  components: {
    TaskRow,
    InlineRichTextEditorViewer,
    EditTaskDrawer,
    ParticipantListModal,
  },
  props: {
    milestone: {
      type: Object as PropType<Milestone>,
      required: true,
    },
    owner: {
      type: Object as PropType<Participant>,
      default: () => ({ id: '', contactId: '', role: ParticipantRole.Owner }),
    },
    participantId: {
      type: String,
      default: '',
    },
    participants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    milestoneOrder: {
      type: Number,
      default: 1,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const {
      taskToEdit,
      showTaskDrawer,
      expanded,
      isSmallScreenAndDown,
      formattedDescription,
      progress,
      taskOwners,
      showTaskOwnersModal,
      selectableParticipants,
      handleOpenTaskDrawer,
    } = useMilestone(props);

    const handleUpdateTaskStatus = (payload: { status: TaskStatus; taskId: string }) => {
      emit('update-task-status', payload);
      showTaskDrawer.value = false;
    };

    return {
      taskToEdit,
      expanded,
      progress,
      showTaskDrawer,
      formattedDescription,
      isSmallScreenAndDown,
      showTaskOwnersModal,
      selectableParticipants,

      getProgress: MilestoneHelper.getProgress,
      isCompleted: MilestoneHelper.isCompleted,
      getStatus: MilestoneHelper.getStatus,
      taskOwners,
      handleOpenTaskDrawer,
      handleUpdateTaskStatus,
      t,

      SlideOrigin,
      AvatarSize,
      IconSize,
      ButtonType,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/milestone';
@import '@getaccept/dsr-shared-new/src/scss/action-plan-input';
</style>
